import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, cn } from '@heltarchat/ui';
import { Trash2, UserPlus } from 'lucide-react';

import { useTestWidgetUsers } from './hooks/useTestWidgetUsers';

interface TestUsersPickerProps {
  businessId: number | undefined;
  employeeId: number | undefined;
}

export const TestUsersPicker = memo(function TestUsersPicker({
  businessId,
  employeeId,
}: TestUsersPickerProps) {
  const { t } = useTranslation();
  const { users, activeId, addUser, switchUser, deleteUser } =
    useTestWidgetUsers(businessId, employeeId);
  const [newName, setNewName] = useState<string>('');

  const create = (): void => {
    void addUser(newName);
    setNewName('');
  };

  return (
    <div className='space-y-2'>
      {users.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {users.map(u => {
            const isActive = activeId === u.id;
            return (
              <div
                key={u.id}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs',
                  isActive
                    ? 'border-[var(--heltar-brand-primary)] bg-[var(--heltar-bg-surface-success)] text-[var(--heltar-text-primary)]'
                    : 'border-[var(--heltar-border-subtle)] bg-[var(--heltar-bg-surface-elevated)] text-[var(--heltar-text-secondary)]',
                )}
              >
                <button
                  type='button'
                  onClick={() => switchUser(u)}
                  className='font-medium'
                  title={t('Test as this user')}
                >
                  {u.name || t('Default')}
                  {isActive && ' ✓'}
                </button>
                <button
                  type='button'
                  onClick={() => void deleteUser(u)}
                  aria-label={t('Delete {{name}}', {
                    name: u.name || t('test user'),
                  })}
                  className='inline-flex items-center justify-center rounded hover:text-[var(--heltar-state-error)] focus:outline-none'
                >
                  <Trash2 className='h-3 w-3' />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className='flex gap-2'>
        <Input
          value={newName}
          placeholder={t('New test user name (optional)')}
          className='h-9 !border-[1px] border-[var(--heltar-border-input)] focus:border-[var(--heltar-border-input-focus)]'
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              create();
            }
          }}
        />
        <Button
          type='button'
          variant='outline'
          className='h-9 shrink-0 border-[var(--heltar-border-input)] hover:border-[var(--heltar-border-button-hover)]'
          onClick={create}
        >
          <UserPlus className='mr-1 h-4 w-4' />
          {t('New user')}
        </Button>
      </div>
    </div>
  );
});
